import { 
  DatabaseConfig,
  EmailConfig, 
  SMSConfig,
  ReferralURLConfig,
  ReferralTemplateConfig,
  PointsValueConfig,
  SignupFormConfig,
  SignupFormField,
  SavedConfigurations,
  TestConnectionResult,
} from './types';

// Re-export all types
export type {
  DatabaseConfig,
  EmailConfig, 
  SMSConfig,
  ReferralURLConfig,
  ReferralTemplateConfig,
  PointsValueConfig,
  SignupFormConfig,
  SignupFormField,
  SavedConfigurations,
  TestConnectionResult,
};

// Re-export functions from other modules
export { testDatabaseConnection, saveDatabaseConfig } from './database';
export { testEmailConnection, saveEmailConfig } from './email';
export { testSMSConnection, saveSMSConfig } from './sms';
export { 
  saveSignupForm,
  getSignupForm,
} from './signupForm';

export { 
  saveReferralURL, 
  deleteReferralURL, 
  saveReferralTemplate,
  deleteReferralTemplate,
  getReferralURLs,
  getReferralTemplates,
  getReferralTemplatesByCampaign,
  getDefaultTemplateForCampaign,
  savePointsValue,
} from './referral';

// Add missing functions that are needed
export const getDefaultPointsValue = (): PointsValueConfig => {
  return { pointsValue: 10, currency: 'USD' };
};

export const getDefaultReferralURLs = (): ReferralURLConfig[] => {
  return [];
};

// Dummy implementation for demonstration purposes
// In a real app, these would interact with a backend API

// Get saved configurations from local storage or API
export const getSavedConfigurations = async (): Promise<SavedConfigurations> => {
  // In a real app, this would be an API call
  // This is a simplified implementation for demonstration purposes
  const savedConfig = localStorage.getItem('savedConfigurations');
  
  if (savedConfig) {
    return JSON.parse(savedConfig);
  } else {
    // Default empty configurations
    return {
      database: undefined,
      sms: undefined,
      email: undefined,
      referralUrls: [],
      referralTemplates: [],
      pointValue: undefined,
      signupForm: undefined
    };
  }
};

// Save configurations to local storage or API
export const saveConfigurations = async (configType: string, config: any): Promise<boolean> => {
  try {
    // Get existing configs
    const savedConfigs = await getSavedConfigurations();
    
    // Update the specific configuration
    const updatedConfigs = {
      ...savedConfigs,
      [configType]: config
    };
    
    // Save to localStorage
    localStorage.setItem('savedConfigurations', JSON.stringify(updatedConfigs));
    
    return true;
  } catch (error) {
    console.error('Error saving configurations:', error);
    return false;
  }
};

// Other configurations for app
export const appConfig = {
  name: 'Referral Program',
  version: '1.0.0',
  environment: import.meta.env.MODE || 'development',
  supportEmail: 'support@referralprogram.com',
  logoUrl: '/logo.png',
  privacyPolicyUrl: '/privacy-policy',
  termsOfServiceUrl: '/terms-of-service'
};

// System settings
export const systemSettings = {
  maintenanceMode: false,
  maintenanceMessage: 'System is currently under maintenance. Please check back later.',
  debugMode: import.meta.env.DEV || false,
  apiBaseUrl: import.meta.env.VITE_API_URL || 'https://api.example.com',
  logLevel: 'info',
  sessionTimeout: 60 * 30 // 30 minutes
};

// User settings
export const userSettings = {
  defaultLanguage: 'en',
  defaultTheme: 'light',
  defaultCurrency: 'USD'
};

// Security settings
export const securitySettings = {
  passwordMinLength: 8,
  passwordRequiresSymbol: true,
  passwordRequiresNumber: true,
  passwordRequiresUppercase: true,
  maxLoginAttempts: 5,
  lockoutTimeMinutes: 30
};

// Data persistence (mock implementation)
export const saveSettings = async (key: string, value: any): Promise<boolean> => {
  try {
    localStorage.setItem(`config_${key}`, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error('Error saving settings:', error);
    return false;
  }
};

// Load settings (mock implementation)
export const loadSettings = async (key: string, defaultValue: any = null): Promise<any> => {
  try {
    const value = localStorage.getItem(`config_${key}`);
    return value ? JSON.parse(value) : defaultValue;
  } catch (error) {
    console.error('Error loading settings:', error);
    return defaultValue;
  }
};

// Reset settings to defaults (mock implementation)
export const resetSettings = async (key?: string): Promise<boolean> => {
  try {
    if (key) {
      localStorage.removeItem(`config_${key}`);
    } else {
      const keys = Object.keys(localStorage);
      keys.forEach(k => {
        if (k.startsWith('config_')) {
          localStorage.removeItem(k);
        }
      });
    }
    return true;
  } catch (error) {
    console.error('Error resetting settings:', error);
    return false;
  }
};
